/**
 * Bumps the tree scroll version when the selection set changes but the primary
 * stays the same (box/lasso via pickKeptPrimary). The [mainObject] effect does
 * not run in that case, and ancestor loads can still shift the primary's row.
 *
 * Runs only when `mainObjects` changes. A later path or search-clear update
 * must not bump again — that second bump resets the in-flight scroll guard and
 * can fulfill against the pre-ancestor list. The scroll target is the current
 * primary's row, resolved in this same update. `selectedPath` is the last
 * canvas selection and is stale or still unset after a tree click.
 *
 * A tree edit that keeps the primary sets `treeSelectedMainObjectRef` to that
 * primary and is skipped. The ref is consumed here because the [mainObject]
 * effect will not run to clear it. Any other id is a stale arm (an aborted row
 * click) and is dropped so it cannot swallow this canvas update. A tree edit
 * that changes the primary leaves the ref for that effect.
 */
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef } from "react";

export function useRescrollOnSamePrimary({
    mainObjects,
    mainObject,
    isFilterActive,
    treeSelectedMainObjectRef,
    setScrollToVersion,
    setSelectedPath,
    resolveMainObjectPath,
}: {
    mainObjects: number[];
    mainObject: number | undefined;
    isFilterActive: boolean;
    treeSelectedMainObjectRef: MutableRefObject<number | undefined>;
    setScrollToVersion: Dispatch<SetStateAction<number>>;
    setSelectedPath: Dispatch<SetStateAction<string | undefined>>;
    resolveMainObjectPath: (id: number) => string | undefined;
}): void {
    const prevMainObjectRef = useRef(mainObject);
    // Read during render, before either effect. The effect below writes the ref
    // after paint, so this still sees the previous commit's primary. Writing the
    // ref during render would stick across the StrictMode replay and make a real
    // primary change look unchanged. Effect order does not matter; moving the
    // comparison into an effect would.
    const mainObjectUnchanged = mainObject === prevMainObjectRef.current;
    // Same mainObjects reference means this effect run is the StrictMode replay
    // of a decision we already made, not a new selection set.
    const handledMainObjectsRef = useRef<number[] | null>(null);

    useEffect(() => {
        if (!mainObjectUnchanged) {
            return;
        }
        if (handledMainObjectsRef.current === mainObjects) {
            return;
        }
        const treeSelectionId = treeSelectedMainObjectRef.current;
        if (treeSelectionId !== undefined && treeSelectionId !== mainObject) {
            treeSelectedMainObjectRef.current = undefined;
        } else if (treeSelectionId === mainObject && mainObject !== undefined) {
            handledMainObjectsRef.current = mainObjects;
            treeSelectedMainObjectRef.current = undefined;
            return;
        }
        // The primary's row is already in the tree on a same-primary canvas
        // change. Set that path in the same update as the version bump so a
        // stale or missing selectedPath cannot scroll to the previous object.
        const path = mainObject !== undefined && !isFilterActive ? resolveMainObjectPath(mainObject) : undefined;
        if (path) {
            handledMainObjectsRef.current = mainObjects;
            setSelectedPath(path);
            setScrollToVersion((version) => version + 1);
        }
        // mainObject, isFilterActive, mainObjectUnchanged, and the path resolver
        // are read from the render that changed mainObjects. Re-running when they
        // change restarts an in-flight scroll or jumps the tree when search clears.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainObjects]);

    useEffect(() => {
        prevMainObjectRef.current = mainObject;
    }, [mainObject]);
}
